import { ReactElement, useContext, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Button, Divider, Fab, Grid, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { ArrowBackOutlined, KeyboardArrowRightOutlined } from '@mui/icons-material'
import { LanguageContext } from '../../../utilities/contexts/LanguageContext'
import language from '../../../language/language'
import Colors from '../../../assets/Colors'
import printImage from '../../../assets/ic-print.png'
import { ReturnDetailsStyles } from './ReturnDetailsStyles'

const ReturnDetails = () => {
    const navigate = useNavigate()
    const { currentLanguage } = useContext(LanguageContext)
    const windowSize = useRef([window.innerWidth, window.innerHeight])
    const selectedReturn = JSON.parse(useLocation().state.item)

    const handleGoBack = async () => {
        navigate(-1)
    }

    const renderBackButton = () => {
        return (
            <Grid xs={12} sx={ReturnDetailsStyles.gridDetail} item>
                <Button
                    onClick={handleGoBack}
                    style={{ padding: 0 }}
                    data-testid='go-back'>
                    <ArrowBackOutlined fontSize='medium' style={{ padding: 0, color: Colors.napaGrey2 }} />
                    <Typography data-testid='back_button' sx={ReturnDetailsStyles.backButton}>
                        {language[currentLanguage].back}
                    </Typography>
                </Button>
            </Grid>
        )
    }

    const renderTitle = () => {
        return (
            <>
                <Grid xs={12} sx={ReturnDetailsStyles.gridDetail} marginTop={1} item>
                    <Typography sx={ReturnDetailsStyles.title}>
                        {selectedReturn.customerName}
                    </Typography>
                </Grid><Grid xs={12} sx={ReturnDetailsStyles.gridDetail} marginTop={'0px'} item>
                    <Typography>
                        Return Number: <strong>#{selectedReturn.returnNumber}</strong>
                    </Typography>
                </Grid>
            </>
        )
    }

    const renderContent = () => {
        return (
            <>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            <Fab color={'success'} size='small' style={{ boxShadow: 'none' }}>
                                1
                            </Fab>
                        </Grid>
                        <Grid item padding={0}>
                            <Typography margin={'0px 10px'}>
                                {language[currentLanguage].returnStarted}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Divider />
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail} >
                        <Typography marginTop='0px' marginLeft='50px' fontSize={'14px'}>
                        {language[currentLanguage].returnStartedDesc}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            <Fab size='small' style={{ boxShadow: 'none', color: Colors.napaGreyDisabled2, backgroundColor: 'white', border: `1px solid ${Colors.napaGreyDisabled2}` }}>
                                2
                            </Fab>
                        </Grid>
                        <Grid item padding={'0px'}>
                            <Typography margin={'0px 10px'} color={Colors.napaGreyDisabled2}>
                            {language[currentLanguage].partsArrived}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Divider />
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail}>
                        <Typography>

                        </Typography>
                    </Grid>
                </Grid>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            <Fab size='small' style={{ boxShadow: 'none', color: Colors.napaGreyDisabled2, backgroundColor: 'white', border: `1px solid ${Colors.napaGreyDisabled2}` }}>
                                3
                            </Fab>
                        </Grid>
                        <Grid item padding={'0px'}>
                            <Typography margin={'0px 10px'} color={Colors.napaGreyDisabled2}>
                            {language[currentLanguage].partsReview}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Divider />
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail}>
                        <Typography>

                        </Typography>
                    </Grid>
                </Grid>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            <Fab size='small' style={{ boxShadow: 'none', color: Colors.napaGreyDisabled2, backgroundColor: 'white', border: `1px solid ${Colors.napaGreyDisabled2}` }}>
                                4
                            </Fab>
                        </Grid>
                        <Grid item padding={'0px'}>
                            <Typography margin={'0px 10px'} color={Colors.napaGreyDisabled2}>
                            {language[currentLanguage].sendToTAMS}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail} >
                        <Typography>

                        </Typography>
                    </Grid>
                </Grid>
            </>
        )
    }

    const ReturnDetailsGrid: ReactElement = useMemo(() => {
        const columns = [
            {
                field: 'line',
                headerName: 'LINE',
                minWidth: 100,
                editable: false,
            },
            {
                field: 'partNumber',
                headerName: 'PART NUMBER',
                minWidth: 150,
                editable: false,
                filterable: false,
            },
            {
                field: 'description',
                headerName: 'DESCRIPTION',
                minWidth: 250,
                editable: false,
                filterable: false,
            },
            {
                field: 'quantity',
                headerName: 'QTY',
                minWidth: 100,
                editable: false,
            },
            {
                field: 'type',
                headerName: 'TYPE',
                minWidth: 100,
                editable: false,
                filterable: false,
            },
            {
                field: 'added',
                headerName: 'ADDED',
                minWidth: 100,
                editable: false,
                filterable: false,
            },
            {
                field: 'notes',
                headerName: 'NOTES',
                minWidth: 250,
                editable: false,
                filterable: false,
            },
            {
                field: 'status',
                headerName: 'STATUS',
                minWidth: 200,
                editable: false,
                filterable: false,
            }
        ]

        const renderDataGrid = () => {
            return (
                <DataGrid
                    getRowId={(row) => `${row.line}${row.index}`}
                    disableColumnMenu
                    style={{ height: `${windowSize.current[1] - 450}px` }}
                    disableColumnSelector={true}
                    hideFooterPagination={true}
                    rows={selectedReturn.partDetails?.map((returns: any, index: number) => {
                        const row = {
                            line: returns.productLine,
                            partNumber: returns.partNumber,
                            description: returns.description,
                            quantity: returns.qty,
                            type: returns.partType,
                            added: returns.isPartScanned ? 'Scanned' : 'Manual',
                            notes: returns.notes,
                            status: returns.partStatus,
                            index: index
                        }
                        return row
                    }) || []}
                    columns={columns}
                    checkboxSelection={false} //TODO for validation screen
                    hideFooter
                    hideFooterSelectedRowCount={false}
                    disableVirtualization={process.env.NODE_ENV === 'test'}
                    sx={ReturnDetailsStyles.gridSize}
                />
            )
        }
        return renderDataGrid()
    }, [selectedReturn])

    return (
        <Grid container spacing={1} sx={ReturnDetailsStyles.gridContainer}>
            {renderBackButton()}
            {renderTitle()}
            {renderContent()}
            <Grid marginLeft={'auto'}>
                <Button
                    onClick={() => {}}
                    sx={ReturnDetailsStyles.disableText}
                    style={{ borderRight: '1px solid blue', borderRadius: '0', padding: '0 8px' }}
                    data-testid='view-signature'>
                    View Signature
                </Button>
                <Button
                    onClick={() => {}}
                    sx={ReturnDetailsStyles.disableText}
                    style={{ padding: '0 8px' }}
                    data-testid='print-slip'>
                    {language[currentLanguage].print}
                    <Avatar aria-label='print-icon' sx={ReturnDetailsStyles.printIcon} src={printImage} alt='print'></Avatar>
                </Button>
            </Grid>
            <Grid item xs={12}>
                {ReturnDetailsGrid}
            </Grid>
            <Button
                onClick={() => { }}
                variant='primary'
                sx={{ marginLeft: 'auto', marginTop: '10px' }}
                data-testid='confirm-returns'>
                {language[currentLanguage].confirmParts}
                <KeyboardArrowRightOutlined fontSize='large' />
            </Button>
        </Grid>
    )
}

export default ReturnDetails